/**
 * Read-only Settings panel for the service detail page (DNKADM-0295) — the second
 * column beside Assignments. Surfaces the nine booking/display settings that today are
 * visible only inside {@link ServiceDialog}'s edit form, grouped **Booking rules**
 * (how the service behaves at booking time) and **Display** (how it presents, no
 * booking consequence).
 *
 * **Read-only by design.** This panel renders values (text/chips), never controls —
 * a greyed-out `Switch` would read as "you can't change this," which is false. The
 * single write path stays {@link ServiceDialog}, opened here via the `onEdit`
 * affordance (`setEditOpen(true)` in the caller).
 *
 * **Label reuse.** `guestSummary`/`paymentModeSummary` (`./serviceLabels` — split out of
 * `ServiceDetail.tsx`, which originally defined them, into a plain module so exporting
 * them doesn't break that file's React Fast Refresh) and `assignmentModeLabel`
 * (`@/domain/v2Enums`) are the SAME helpers the retired header summary line used —
 * reused here rather than re-derived, so there stays exactly one place each label is
 * produced. The two help-text captions (requires confirmation; payment at booking when
 * `card_on_file`) are copied verbatim from {@link ServiceDialog}'s `FormHelperText`
 * copy — {@link ServiceDialog} itself is untouched (Non-Goal), so these are literal
 * string copies, not a shared export.
 *
 * **Absent-vs-false discipline (DNKADM-0256/0290).** `variationDisplay`/`featured` are
 * genuinely optional on `V2Service` (the wire may omit them) — an absent value renders
 * as `—`, never a guessed default. The other seven settings are non-optional booleans/
 * enums on the type (the API wrapper's defensive parser always resolves them to a
 * concrete value), so there is no absent state to represent for those.
 *
 * **Credit bucket (Operator-approved scope addition).** Resolving `creditBucketId` to
 * a friendly name requires a fetch this detail page did not previously make; the
 * Operator approved adding it (mirroring `ServicesScreen`'s `listCreditBuckets`
 * pattern) with a hard fail-soft requirement: `bucketNameById` is passed in already
 * resolved (or empty, on a load failure) by the caller, and an id this map can't
 * resolve renders `—` — the bucket-name read never gates or breaks the other eight
 * settings. See the DNKADM-0295 delivery's Notes for Architect for why this reopened
 * the spec's original "no new read."
 */
import type { ReactNode } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, resolveTokens } from '@/theme';
import { assignmentModeLabel } from '@/domain/v2Enums';
import { LEAD_TIME_BEHAVIOUR, leadTimeMinutesLabel } from '@/domain/leadTime';
import type { V2Service } from '@/api/services';
import { guestSummary, paymentModeSummary } from './serviceLabels';

export interface ServiceSettingsPanelProps {
  /** The loaded service (detail read — all nine settings are drawn from this). */
  service: V2Service;
  /**
   * Credit bucket id → display name, resolved by the caller (fail-soft: empty while
   * loading or on a failed read — an unresolvable id renders `—`, never the raw id).
   */
  bucketNameById: Map<string, string>;
  /** Opens the existing edit dialog (`setEditOpen(true)` in the caller). */
  onEdit: () => void;
}

/** Requires-confirmation helper copy — verbatim from `ServiceDialog`'s `FormHelperText`. */
const REQUIRES_CONFIRMATION_HINT = 'Bookings need operator approval before they’re confirmed.';

/**
 * Payment-at-booking `card_on_file` guidance — verbatim from `ServiceDialog`'s
 * `paymentModeHelp` guidance branch (the always-appended future-bookings caveat is
 * dialog-form-specific and is not carried here; keeps the panel scannable).
 */
const CARD_ON_FILE_HINT =
  'The guest’s card is saved at booking with no charge (e.g. swing evaluation). A no-show fee can be charged later.';

/** `—` for a genuinely absent (undefined) optional field — never a guessed default. */
const ABSENT = '—';

export function ServiceSettingsPanel({ service, bucketNameById, onEdit }: ServiceSettingsPanelProps) {
  const creditBucketValue =
    service.creditBucketId === null
      ? 'None — free service'
      : (bucketNameById.get(service.creditBucketId) ?? ABSENT);

  const variationDisplayValue =
    service.variationDisplay === undefined
      ? ABSENT
      : service.variationDisplay === 'duration'
        ? 'Durations'
        : 'Named types';

  return (
    <Box role="region" aria-label="Settings">
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Settings
        </Typography>
        {/* Distinct accessible name from the header's "Edit details" button (both open
            the same dialog) — two same-named buttons on one page would break its
            existing role query in ServiceDetail.test.tsx. */}
        <Button size="small" startIcon={<Icon name="edit" />} onClick={onEdit}>
          Edit settings
        </Button>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        How this service behaves at booking, and how it presents. Change these in Edit details.
      </Typography>

      <SettingsGroup title="Booking rules">
        <SettingRow label="Staff selection" value={assignmentModeLabel(service.staffAssignment)} />
        <SettingRow label="Resource selection" value={assignmentModeLabel(service.resourceAssignment)} />
        <SettingRow
          label="Requires confirmation"
          value={
            service.requiresConfirmation ? (
              <Chip size="small" color="warning" label="Confirmation required" />
            ) : (
              'No confirmation needed'
            )
          }
          hint={REQUIRES_CONFIRMATION_HINT}
        />
        <SettingRow
          label="Payment at booking"
          value={paymentModeSummary(service.paymentMode)}
          {...(service.paymentMode === 'card_on_file' ? { hint: CARD_ON_FILE_HINT } : {})}
        />
        <SettingRow label="Credit bucket" value={creditBucketValue} />
        {/* Booking lead time (DNKADM-0385) — read from the same service GET, no new
            read. The inherited tenant figure is shown in the edit dialog, which is
            where the tenant-settings read lives. */}
        <SettingRow
          label="Booking lead time"
          value={
            service.leadTimeMinutes === undefined
              ? 'Inherits the tenant lead time'
              : `${leadTimeMinutesLabel(service.leadTimeMinutes)} (override)`
          }
          hint={LEAD_TIME_BEHAVIOUR}
        />
        <SettingRow label="Allow guests" value={guestSummary(service)} />
        <SettingRow label="Taxable" value={service.taxable ? 'Taxed at booking' : 'Not taxable'} last />
      </SettingsGroup>

      <SettingsGroup title="Display" last>
        <SettingRow label="Show variations as" value={variationDisplayValue} />
        <SettingRow
          label="Featured in the member app"
          value={
            service.featured === undefined ? (
              ABSENT
            ) : service.featured ? (
              <Chip size="small" color="success" label="Featured" />
            ) : (
              'Not featured'
            )
          }
          last
        />
      </SettingsGroup>
    </Box>
  );
}

/** One labeled group of {@link SettingRow}s (a section heading + its rows). */
function SettingsGroup({ title, last, children }: { title: string; last?: boolean; children: ReactNode }) {
  return (
    <Box sx={{ mb: last ? 0 : 3 }}>
      <Typography
        variant="overline"
        sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5, mb: 0.5 }}
      >
        {title}
      </Typography>
      <Stack>{children}</Stack>
    </Box>
  );
}

/**
 * One read-only label/value row — text or a `Chip` value (never a `Switch`, §Scope 2).
 * `hint`, when given, renders as a caption beneath the label (the two settings easy to
 * misread, §Scope 4). `last` drops the row divider (the group's final row).
 */
function SettingRow({
  label,
  value,
  hint,
  last,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  last?: boolean;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.25,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{label}</Typography>
        {hint && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            {hint}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', pt: 0.25 }}>
        {typeof value === 'string' ? <Typography variant="body2">{value}</Typography> : value}
      </Box>
    </Stack>
  );
}
